import React from "react";
import { MDBTooltip } from "mdb-react-ui-kit";
import {
  Info,
  ArrowUp,
  ArrowDown,
  Minus,
  RefreshCw,
  FileText,
  CreditCard,
  BarChart3,
} from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import SARIcon from "../SARIcon";

const formatValue = (val) => {
  if (val === null || val === undefined || isNaN(val)) return 0;
  return val;
};

const parsePct = (p) => {
  if (typeof p === "number") return p;
  if (p == null || p === "") return null;
  const n = parseFloat(String(p).replace("%", "").trim());
  return Number.isNaN(n) ? null : n;
};

const formatPctLabel = (n) => {
  const rounded = Math.round(Math.abs(n) * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded.toFixed(1)}%`;
};

function TrendIcon({ dir, size = 11 }) {
  const props = { size, strokeWidth: 2.5, "aria-hidden": true };
  if (dir === "down") return <ArrowDown {...props} />;
  if (dir === "flat") return <Minus {...props} />;
  return <ArrowUp {...props} />;
}

function DashboardMetricIcon({ name }) {
  const stroke = 1.5;
  if (name === "orders") return <FileText size={14} strokeWidth={stroke} color="#ffffff" />;
  if (name === "payment") return <CreditCard size={18} strokeWidth={stroke} color="#ffffff" />;
  if (name === "sales") return <BarChart3 size={18} strokeWidth={stroke} color="#ffffff" />;
  return (
    <span className="oh-kpi-dash__icon-aov">
      <RefreshCw size={18} strokeWidth={1.5} color="#ffffff" />
      <span className="oh-kpi-dash__icon-dollar">
        <SARIcon size={9} color="#ffffff" />
      </span>
    </span>
  );
}

/**
 * KPI card
 * default — design-system State Card (Reports, Admin)
 * orders — Figma Onehub Copy node 1-3095 (value on top)
 * dashboard — Figma Onehub Copy node 1:2648
 * https://www.figma.com/design/0eZBxhPXqDhmRY87ld8ckv/Onehub--Copy-?node-id=1-2648
 */
export function KpiCard({
  title,
  value,
  unit,
  prefix,
  icon,
  subtitle,
  subLabel,
  trend,
  trendValue,
  percentage,
  accentColor,
  borderColor,
  breakdown,
  evenFooter = false,
  loading = false,
  variant = "default",
  accented = false,
  className = "",
}) {
  const accent = accentColor || borderColor;
  const isDashboard = variant === "dashboard";
  const isOrders = variant === "orders";
  const cardClass = [
    "oh-kpi-card",
    "oh-stat-card",
    isDashboard ? "oh-kpi-card--dashboard" : "",
    isOrders ? "oh-kpi-card--orders" : "",
    accented ? "oh-kpi-card--accented oh-stat-card--accented" : "",
    loading ? "oh-kpi-card--loading" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const pct = parsePct(percentage ?? trendValue);
  const trendDir =
    trend ||
    (typeof pct === "number" ? (pct < 0 ? "down" : pct > 0 ? "up" : "flat") : "flat");
  const isDown = trendDir === "down";
  const isFlat = trendDir === "flat" || pct === 0;
  const displayPct =
    pct != null
      ? formatPctLabel(pct)
      : percentage != null
        ? String(percentage).replace(/^-/, "")
        : null;

  const showInlineTrend = variant !== "report" && trend && trendValue && pct == null;
  const showPctTrend = Boolean(displayPct);
  const valueIsText =
    className?.includes("oh-kpi-card--text-value") ||
    (typeof value === "string" && value.length > 0 && !/\d/.test(value));

  const renderedValue =
    typeof value === "number" ? formatValue(value).toLocaleString() : value;

  const trendClass = isFlat ? "flat" : isDown ? "down" : "up";

  return (
    <div
      className={cardClass}
      style={accented && accent ? { "--oh-stat-accent": accent } : undefined}
    >
      {isDashboard ? (
        <div className="oh-kpi-card__body oh-kpi-dash">
          <div className="oh-kpi-dash__top">
            <span className="oh-kpi-dash__icon" aria-hidden="true">
              {loading ? (
                <Skeleton width={22} height={22} />
              ) : (
                <DashboardMetricIcon name={icon} />
              )}
            </span>
            {loading ? (
              <Skeleton width={56} height={16} />
            ) : showPctTrend ? (
              <span className={`oh-kpi-dash__trend oh-kpi-dash__trend--${trendClass}`}>
                <TrendIcon dir={isFlat ? "flat" : trendDir} size={11} />
                {displayPct}
              </span>
            ) : null}
          </div>

          <div className="oh-kpi-dash__main">
            {loading ? (
              <Skeleton height={19} width="70%" />
            ) : (
              <h2
                className="oh-kpi-dash__value"
                dir={prefix ? "ltr" : undefined}
              >
                {prefix ? <span className="oh-kpi-dash__prefix">{prefix}</span> : null}
                {renderedValue}
                {unit ? <span className="oh-kpi-dash__unit">{unit}</span> : null}
              </h2>
            )}
            <p className="oh-kpi-dash__label">
              {title}
              {subtitle && (
                <MDBTooltip tag="span" wrapperClass="oh-kpi-card__info" title={subtitle}>
                  <Info size={14} style={{ cursor: "pointer" }} />
                </MDBTooltip>
              )}
            </p>
          </div>
        </div>
      ) : (
        <div className="oh-kpi-card__body oh-stat-card__body">
          <div className={isOrders ? "oh-kpi-card__header" : undefined}>
          <div className="oh-kpi-card__label-row oh-stat-card__label-row">
            <p className="oh-kpi-card__label oh-stat-card__label">
              {title}
              {subtitle && (
                <MDBTooltip tag="span" wrapperClass="oh-kpi-card__info" title={subtitle}>
                  <Info size={14} style={{ cursor: "pointer" }} />
                </MDBTooltip>
              )}
            </p>
          </div>

          {loading ? (
            <Skeleton height={38} width="55%" />
          ) : (
            <div className="oh-kpi-card__metrics oh-stat-card__metrics">
              <h2
                className={[
                  "oh-kpi-card__value",
                  "oh-stat-card__value",
                  valueIsText ? "oh-kpi-card__value--text" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {renderedValue}
                {unit ? <span className="oh-kpi-card__unit">{unit}</span> : null}
              </h2>

              {showPctTrend && (
                <span
                  className={`oh-kpi-card__delta oh-stat-card__delta ${
                    isFlat
                      ? "oh-kpi-card__delta--flat"
                      : isDown
                        ? "oh-kpi-card__delta--down"
                        : "oh-kpi-card__delta--up"
                  }`}
                >
                  <TrendIcon dir={isFlat ? "flat" : trendDir} />
                  {displayPct}
                </span>
              )}
            </div>
          )}
          </div>

          {showInlineTrend && !loading && (
            <span className="oh-stat-card__trend">
              {formatValue(trendValue)} {trend}
            </span>
          )}

          {subLabel && !loading && (
            <p className="oh-kpi-card__sublabel oh-stat-card__sublabel">{subLabel}</p>
          )}

          {showPctTrend && loading && <Skeleton height={16} width={72} />}

          {breakdown && breakdown.length > 0 ? (
            <>
              <div className="oh-kpi-card__divider oh-stat-card__divider" role="presentation" />
              <div className="oh-kpi-card__breakdown oh-stat-card__breakdown">
                {breakdown.map((item, index) => (
                  <div
                    key={index}
                    className="oh-kpi-card__breakdown-item oh-stat-card__breakdown-item"
                  >
                    <small className="oh-kpi-card__breakdown-label oh-stat-card__breakdown-label">
                      {item.label}
                    </small>
                    <span className="oh-kpi-card__breakdown-value oh-stat-card__breakdown-value">
                      {formatValue(item.value).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : evenFooter ? (
            <div className="oh-kpi-card__footer-slot" aria-hidden="true" />
          ) : null}
        </div>
      )}
    </div>
  );
}

export default KpiCard;
